import { Component } from './Component';

const MatchAny = 1;
const MatchValue = 2;
const DirectDescendant = 3;
const AnyDescendant = 4;

type TokenOp =
    typeof MatchAny |
    typeof MatchValue |
    typeof DirectDescendant |
    typeof AnyDescendant;

type TokenPredicate = (x: Component) => boolean;
type Token = [TokenOp, TokenPredicate?];

const isEqualCaseInsensitive = (a: string, b: string) => !!a && !!b && a.localeCompare(b, undefined, { sensitivity: 'base' }) === 0;

const createMatchPredicate = (value: string) => {
    if (value.startsWith('@')) {
        value = value.slice(1);
        return (x: Component) => 'tag' in x && isEqualCaseInsensitive(value, (x as any).tag);
    }
    else {
        return (x: Component) => isEqualCaseInsensitive(value, x.type);
    }
};

const createToken = (value: string): Token => {
    if (value === '>') return [DirectDescendant];
    if (value === '~') return [AnyDescendant];
    if (value === '*') return [MatchAny];
    return [MatchValue, createMatchPredicate(value)];
};

const createTokenSequence = (selector: string) => selector
    .split(/\s/g)
    .map(createToken)
    .reverse();

const createMatcher = (tokens: Token[]) =>
    (cmpt: Component): boolean => {
        let scope = [cmpt];

        for (const [op, pred] of tokens) {

            switch (op) {
                case MatchAny:
                    // Do nothing
                    break;
                case MatchValue:
                    scope = scope.filter(pred);
                    break;
                case DirectDescendant:
                    scope = scope.flatMap(x => x.parent ? x.parent : []);
                    break;
                case AnyDescendant:
                    scope = scope.flatMap(x => x.ancestors());
                    break;
            }

            if (scope.length === 0) {
                return false;
            }
        }

        return true;
    };

const compile = (selector: string) => createMatcher(createTokenSequence(selector));

/**
 * Represents an expression that can be used to select matching Components from the component tree.
 */
export interface ComponentSelector {

    /**
     * The original text form of the selector.
     */
    readonly source: string;

    /**
     * Applies the selector to the specified Component and returns a value indicating whether or not it is a match.
     * @param component the component
     */
    match(component: Component): boolean;
}

export const ComponentSelector = Object.freeze({
    compile(selector: string): ComponentSelector {
        return {
            source: selector,
            match: compile(selector),
        };
    },
});
